<!-- This component was created before Reka UI was released.
 Don't overuse this component to minimize migration effort (DEV-8894).
 Reka uses a single component with `multiple` prop to handle both single and multiple select fields.
 -->

<script lang="ts" setup generic="T extends string | number | boolean | Record<string, any>, V extends string | number">
import { capitalize } from '@docue/utils/string'

import type { SelectProps } from './types'

const props = withDefaults(defineProps<SelectProps<T, V>>(), {
  size: 'md',
  getItemText: (option: T) => option.toString(),
})

const { t } = useI18n()
const { t: tg } = useI18n({ useScope: 'global' })

const selected = defineModel<V[]>({ required: true })
const selectTrigger = useTemplateRef('select-trigger')
const selectOptions = useTemplateRef('select-options')

const placeholder = computed(() => props.placeholder ?? capitalize(tg('selectAnOption')))

const displayedPlaceholder = computed(() => {
  const selectedCount = selected.value.length
  return selectedCount
    ? t('item', selectedCount)
    : placeholder.value
})

const toggleItemSelection = (option: V) => {
  const index = selected.value.indexOf(option)
  index > -1
    ? selected.value = selected.value.toSpliced(index, 1)
    : selected.value = [...selected.value, option]
}

const focusFirstOption = async () => {
  await nextTick()
  selectOptions.value?.[0]?.$el?.focus()
}

watch(selected, () => {
  if (!selectTrigger.value?.$el)
    return
  if (selected.value.length) {
    delete selectTrigger.value?.$el.dataset.placeholder
    return
  }
  selectTrigger.value.$el.dataset.placeholder = placeholder.value
})
</script>

<template>
  <UiSelect
    :has-error
    @update:open="isOpen => isOpen && focusFirstOption()"
  >
    <slot name="trigger">
      <UiSelectTrigger
        ref="select-trigger"
        :size
      >
        <slot name="trigger-content">
          <UiSelectValue :placeholder="displayedPlaceholder" />
        </slot>
      </UiSelectTrigger>
    </slot>

    <UiSelectContent :style="fitWidthToTrigger ? 'max-width: var(--reka-popper-anchor-width)' : ''">
      <UiSelectGroup>
        <UiSelectLabel v-if="selectLabel">
          {{ selectLabel }}
        </UiSelectLabel>

        <UiSelectMultipleOptionItem
          v-for="(option) in options"
          ref="select-options"
          :key="getItemValue(option)"
          :is-selected="!!selected?.includes(getItemValue(option))"
          @toggle="toggleItemSelection(getItemValue(option))"
        >
          <slot :option>
            {{ getItemText(option) }}
          </slot>
          <UiSelectItemDescription :value="getItemValue(option).toString()">
            <slot
              name="description"
              :option
            >
              {{ getItemDescription?.(option) }}
            </slot>
          </UiSelectItemDescription>
        </UiSelectMultipleOptionItem>
      </UiSelectGroup>
    </UiSelectContent>
  </UiSelect>
</template>

<i18n lang="json" locale="en-GB">
{
  "item": "{count} item | {count} items"
}
</i18n>

<i18n lang="json" locale="de">
{
  "item": "{count} Element | {count} Elemente"
}
</i18n>

<i18n lang="json" locale="fi">
{
  "item": "{count} kohde | {count} kohdetta"
}
</i18n>

<i18n lang="json" locale="pl">
{
  "item": "{count} element | {count} elementy"
}
</i18n>

<i18n lang="json" locale="sv">
{
  "item": "{count} objekt | {count} objekt"
}
</i18n>
